<template>
  <!-- Figma: HeaderRow h-56 / gap-16 / px-16 / py-4。bg は持たずモーダル本体の半透明白を透かす -->
  <div class="flex h-14 shrink-0 items-center gap-4 px-4 py-1">
    <!-- 検索入力。下線（border-b-0.5 muted）のみ。フォーカス時は gold3 -->
    <div class="flex h-12 flex-1 items-center gap-2 border-b-[0.5px] border-beige3 px-2 focus-within:border-gold3">
      <img alt="" class="size-5 shrink-0 opacity-40" src="@/assets/icon/search/default.svg" />
      <input
        ref="inputRef"
        v-model="inputValue"
        aria-label="Search keyword"
        autocapitalize="none"
        autocomplete="off"
        class="search-input flex-1 truncate bg-transparent font-Spectral text-[20px] leading-7 tracking-[0.5px] text-gray9 outline-none placeholder:text-gray4"
        placeholder='Try "private geisha dinner" or "Hakone ryokan"'
        spellcheck="false"
        type="search"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      <button v-if="inputValue" aria-label="Clear search" class="flex shrink-0 items-center justify-center hover:opacity-70" type="button" @click="clearInput">
        <img alt="" class="size-[18px]" src="@/wabunka-src/features/Search/assets/cancel.svg" />
      </button>
    </div>
    <button aria-label="Close search" class="flex size-8 shrink-0 items-center justify-center hover:opacity-70" type="button" @click="props.onClose">
      <img alt="" class="size-7" src="@/assets/icon/close/black.svg" />
    </button>
  </div>
</template>

<script setup lang="ts">
interface Props {
  modelValue: string;
  onUpdateModelValue: (value: string) => void;
  onClose: () => void;
  // ArrowDown 押下時に呼び出される。結果リストの先頭へフォーカスを移すために親が利用する
  onArrowDown?: () => void;
  debounceMs?: number;
}

const props = defineProps<Props>();

const inputValue = ref(props.modelValue);
const inputRef = ref<HTMLInputElement | null>(null);

// モーダル表示時に自動フォーカスして即入力できるようにする。
// preventScroll: focus 時の scroll-into-view によるパンを抑止する（入力欄は先頭で元々見えている）。
onMounted(() => {
  nextTick(() => inputRef.value?.focus({ preventScroll: true }));
});

watch(
  () => props.modelValue,
  (next) => {
    if (next !== inputValue.value) inputValue.value = next;
  },
);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const handleInput = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  const debounceMs = props.debounceMs ?? 300;
  debounceTimer = setTimeout(() => {
    props.onUpdateModelValue(inputValue.value);
  }, debounceMs);
};

const clearInput = () => {
  inputValue.value = '';
  if (debounceTimer) clearTimeout(debounceTimer);
  props.onUpdateModelValue('');
  inputRef.value?.focus({ preventScroll: true });
};

// キーボード操作: ArrowDown は結果リストへフォーカス移動、Esc は段階的にクリア→閉じる
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown') {
    if (props.onArrowDown) {
      event.preventDefault();
      props.onArrowDown();
    }
    return;
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    // ブラウザ既定の type=search の即時クリア挙動を抑え、自前ロジックに揃える
    if (inputValue.value) {
      clearInput();
      return;
    }
    props.onClose();
  }
};

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<style scoped>
/* type="search" のブラウザネイティブ × を非表示化（自前 cancel ボタンと二重表示になるため） */
.search-input::-webkit-search-cancel-button,
.search-input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}
</style>
